import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n/LanguageContext";

const LOGO_URL = "/elysian-logo.png";

export default function Header() {
  const { t, lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", key: "nav.home", end: true },
    { to: "/garage", key: "nav.garage" },
    { to: "/request", key: "nav.request" },
    { to: "/contact", key: "nav.contact" },
  ];

  const LangSwitcher = ({ className = "" }) => (
    <div
      className={`inline-flex items-center gap-2 text-[12px] tracking-[0.24em] uppercase font-medium border border-white/15 px-3 py-1.5 rounded-sm bg-black/30 ${className}`}
      data-testid="language-switcher"
    >
      <button
        onClick={() => setLang("en")}
        data-testid="lang-en"
        aria-label="Switch to English"
        className={`transition-colors ${
          lang === "en" ? "text-[#C6A15B]" : "text-zinc-300 hover:text-white"
        }`}
      >
        {t("nav.language.en")}
      </button>
      <span className="text-zinc-600">|</span>
      <button
        onClick={() => setLang("gr")}
        data-testid="lang-gr"
        aria-label="Switch to Greek"
        className={`transition-colors ${
          lang === "gr" ? "text-[#C6A15B]" : "text-zinc-300 hover:text-white"
        }`}
      >
        {t("nav.language.gr")}
      </button>
    </div>
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[#0B0B0B]/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-[80px] md:h-[96px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo-link" className="flex items-center shrink-0">
          <img
            src={LOGO_URL}
            alt="Elysian Cars"
            className="h-[44px] md:h-[64px] w-auto object-contain drop-shadow-[0_2px_14px_rgba(198,161,91,0.22)]"
            draggable="false"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.to.slice(1) || "home"}`}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
              {t(l.key)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          <LangSwitcher />
          <Link to="/request" className="btn-gold !px-5 !py-3" data-testid="header-request-cta">
            {t("nav.cta")}
          </Link>
        </div>

        <div className="flex md:hidden items-center gap-4">
          <LangSwitcher />
          <button
            data-testid="mobile-menu-toggle"
            className="text-white p-2 -mr-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="md:hidden bg-[#0B0B0B] border-t border-white/5"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`mobile-nav-${l.to.slice(1) || "home"}`}
                className={({ isActive }) => `nav-link !text-base ${isActive ? "active" : ""}`}
              >
                {t(l.key)}
              </NavLink>
            ))}
            <Link
              to="/request"
              className="btn-gold mt-3 w-full justify-center"
              data-testid="mobile-header-request-cta"
            >
              {t("nav.cta")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
